import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Waves, Shield, BookOpen, Wrench, Award } from 'lucide-react';

const Kitesurfing = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  return (
    <div className="min-h-screen" data-testid="kitesurfing-page">
      {/* Hero with Image */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1690681200064-0302728b3572?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="Kitesurfing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-pink-900/70"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <Waves className="w-20 h-20 mx-auto mb-6 text-pink-400" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('kitesurfing.title')}</h1>
            <p className="text-2xl text-purple-100 max-w-2xl mx-auto">{t('kitesurfing.subtitle')}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center max-w-3xl mx-auto">
            <p className="text-lg text-gray-600 leading-relaxed">{t('kitesurfing.description')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="group bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('kitesurfing.safety')}</h3>
              <p className="text-gray-600 text-lg">{t('kitesurfing.safety_desc')}</p>
            </div>
            <div className="group bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('kitesurfing.courses')}</h3>
              <p className="text-gray-600 text-lg">{t('kitesurfing.courses_desc')}</p>
            </div>
            <div className="group bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('kitesurfing.rental')}</h3>
              <p className="text-gray-600 text-lg">{t('kitesurfing.rental_desc')}</p>
            </div>
          </div>

          {/* Detailed Information Sections */}
          <div className="space-y-16">
            {/* Spot */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Waves className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('kitesurfing.spot_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('kitesurfing.spot_desc')}</p>
                </div>
              </div>
            </div>

            {/* Our School */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('kitesurfing.school_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('kitesurfing.school_desc')}</p>
                </div>
              </div>
            </div>

            {/* Our Courses */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('kitesurfing.courses_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('kitesurfing.courses_detail')}</p>
                </div>
              </div>
            </div>

            {/* Safety */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('kitesurfing.safety_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('kitesurfing.safety_detail')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Images Grid */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_surfapp-manager/artifacts/73dsa388_home4.jpg" 
                alt="Kitesurfing Action 1"
                className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_surfapp-manager/artifacts/lht716qg_revier.jpg" 
                alt="Kitesurfing Revier"
                className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Kitesurfing;